import {
    AIConvoThinkingDisplay,
    AIConvoThinkingStep,
    AIConvoThinkingTool,
} from '@modules/ai-convo/models/ai-convo-thinking.model';
import { AI_CONVO_THINKING_GENERIC_KEYS } from './ai-convo-thinking.utils';

/** Every thought stays visible at least this long. */
const MIN_MS = 1500;
/** Without new events a thought gives way to the generic messages after this. */
const IDLE_MS = 4000;
/** How long each generic message lasts before the next one. */
const GENERIC_MS = 3000;
/** More thoughts than this waiting: the display jumps to the most recent. */
const MAX_QUEUE = 3;

const LAST_GENERIC_INDEX = AI_CONVO_THINKING_GENERIC_KEYS.length - 1;

/**
 * Decides what the thought area shows over time. Events arrive in bursts, the
 * display paces them: a minimum on screen per thought, a ceiling without news,
 * the generic messages as a fallback, a queue that skips ahead when it grows.
 * Disposing it drops whatever is still queued, which is how a quick answer
 * never waits for the thoughts.
 */
export class AIConvoThinkingDisplayController {
    private current: AIConvoThinkingDisplay = { kind: 'generic', index: 0 };
    private shownAt = 0;
    private queue: AIConvoThinkingStep[] = [];
    private advanceTimer: ReturnType<typeof setTimeout> | null = null;
    private stageTimer: ReturnType<typeof setTimeout> | null = null;
    private disposed = false;

    constructor(
        private readonly onChange: (display: AIConvoThinkingDisplay) => void,
    ) {}

    start(): void {
        this.show({ kind: 'generic', index: 0 });
    }

    push(step: AIConvoThinkingStep): void {
        if (this.disposed || step.tool === AIConvoThinkingTool.UNKNOWN) return;

        this.queue.push(step);
        if (this.queue.length > MAX_QUEUE) {
            this.queue = this.queue.slice(-MAX_QUEUE);
        }
        this.scheduleAdvance();
    }

    dispose(): void {
        this.disposed = true;
        this.queue = [];
        this.clearTimers();
    }

    private show(display: AIConvoThinkingDisplay): void {
        this.current = display;
        this.shownAt = Date.now();
        this.clearTimers();
        this.onChange(display);

        if (display.kind === 'thought' || display.index === 0) {
            this.stageTimer = setTimeout(() => this.onIdle(), IDLE_MS);
        } else if (display.index < LAST_GENERIC_INDEX) {
            this.stageTimer = setTimeout(
                () => this.showNextGeneric(),
                GENERIC_MS,
            );
        }

        if (this.queue.length) this.scheduleAdvance();
    }

    private onIdle(): void {
        if (this.queue.length) {
            this.advance();
            return;
        }
        this.show({ kind: 'generic', index: 1 });
    }

    private showNextGeneric(): void {
        if (this.queue.length) {
            this.advance();
            return;
        }
        const index =
            this.current.kind === 'generic'
                ? Math.min(this.current.index + 1, LAST_GENERIC_INDEX)
                : 1;
        this.show({ kind: 'generic', index });
    }

    private scheduleAdvance(): void {
        if (this.advanceTimer) clearTimeout(this.advanceTimer);

        const wait = Math.max(0, this.shownAt + MIN_MS - Date.now());
        this.advanceTimer = setTimeout(() => {
            this.advanceTimer = null;
            this.advance();
        }, wait);
    }

    private advance(): void {
        const next = this.queue.shift();
        if (!next || this.disposed) return;
        this.show({ kind: 'thought', step: next });
    }

    private clearTimers(): void {
        if (this.advanceTimer) clearTimeout(this.advanceTimer);
        if (this.stageTimer) clearTimeout(this.stageTimer);
        this.advanceTimer = null;
        this.stageTimer = null;
    }
}
